import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import type { Service, Tenant, ServiceCategory } from '../../lib/db/types';
import { getPublicBookingCatalog } from '../../lib/bookingApi';

interface ServiceWithId extends Service {
  id: string;
}

interface ServiceCategoryWithId extends ServiceCategory {
  id: string;
}

interface TenantWithId extends Tenant {
  id: string;
}

export function BookingPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenant, setTenant] = useState<TenantWithId | null>(null);
  const [services, setServices] = useState<ServiceWithId[]>([]);
  const [categories, setCategories] = useState<ServiceCategoryWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantSlug) {
      setError('Invalid booking link');
      setLoading(false);
      return;
    }

    const loadCatalog = async () => {
      try {
        const catalog = await getPublicBookingCatalog(tenantSlug);
        setTenant(catalog.tenant as TenantWithId);
        setCategories(catalog.categories as ServiceCategoryWithId[]);
        setServices(catalog.services as ServiceWithId[]);
      } catch (err: any) {
        console.error('Error loading booking catalog:', err);
        setError('Failed to load booking information');
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [tenantSlug]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return 'Free';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const getCategoryName = (categoryId?: string): string | null => {
    if (!categoryId) return null;
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || null;
  };

  const handleSelectService = (serviceId: string) => {
    navigate(`/book/${tenantSlug}/service/${serviceId}`);
  };

  // Get tenant branding color
  const primaryColor = tenant?.branding?.primaryColor || theme.palette.primary.main;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Booking not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pb: 4,
      }}
    >
      {/* Header with Tenant Branding */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {/* Tenant Logo */}
            {tenant.branding?.logoUrl ? (
              <Box
                component="img"
                src={tenant.branding.logoUrl}
                alt={tenant.name}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 1,
                  bgcolor: primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 'bold',
                }}
              >
                {tenant.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight={600}
                sx={{ color: 'text.primary' }}
              >
                {tenant.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book an appointment
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg">
        {/* Page Title */}
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="h2"
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          Select a Service
        </Typography>

        {/* No Services State */}
        {services.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No services available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please check back later or contact the business directly.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          /* Services Grid */
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {services.map((service) => {
              const categoryName = getCategoryName(service.categoryId);
              return (
                <Grid item xs={12} sm={6} lg={4} key={service.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'visible',
                    }}
                    onClick={() => handleSelectService(service.id)}
                  >
                    {/* Color indicator strip at top */}
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: service.color || primaryColor,
                        borderRadius: '4px 4px 0 0',
                      }}
                    />

                    {/* Service Image */}
                    {service.imageUrl && (
                      <Box
                        component="img"
                        src={service.imageUrl}
                        alt={service.name}
                        sx={{
                          width: '100%',
                          height: { xs: 140, sm: 160 },
                          objectFit: 'cover',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    )}

                    <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 } }}>
                      {/* Category Badge */}
                      {categoryName && (
                        <Chip
                          label={categoryName}
                          size="small"
                          sx={{
                            mb: 1.5,
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            height: 24,
                          }}
                        />
                      )}

                      {/* Service Name */}
                      <Typography
                        variant="h6"
                        component="h3"
                        fontWeight={600}
                        sx={{
                          mb: 1,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}
                      >
                        {service.name}
                      </Typography>

                      {/* Description */}
                      {service.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.5,
                          }}
                        >
                          {service.description}
                        </Typography>
                      )}

                      {/* Service Details */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'text.secondary',
                          }}
                        >
                          <AccessTimeIcon fontSize="small" />
                          <Typography variant="body2">
                            {formatDuration(service.duration)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'text.secondary',
                          }}
                        >
                          <AttachMoneyIcon fontSize="small" />
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(service.price, service.currency)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Select Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          mt: 'auto',
                          bgcolor: primaryColor,
                          '&:hover': {
                            bgcolor: primaryColor,
                            filter: 'brightness(0.9)',
                          },
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                        onClick={(e: MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          handleSelectService(service.id);
                        }}
                      >
                        Book Now
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              FIT
            </Box>
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              Stac
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default BookingPage;
